import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { I18n } from 'react-redux-i18n';
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	Select,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import WeeklyAvailability from './WeeklyAvailability';
import * as timeslotActions from '../../../../service/timeslot/actions';
import { notify } from '../../../../Components/Notifier/services/actions';
import { deepEqual } from '../../../../utils';
import { getConfiguredByProduct, getFlatProducts } from './selector';

const TIMESLOT_MODE = { ENABLE: 'enable', DISABLE: 'disable' };
const DEFAULT_MODE = TIMESLOT_MODE.ENABLE;

// comparaison tolérante number/string (l'id peut arriver en string)
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

// index = timeslot.day. 1 = Lundi ... 7 = Dimanche
const DAY_KEYS = [
	null,
	'general.monday',
	'general.tuesday',
	'general.wednesday',
	'general.thursday',
	'general.friday',
	'general.saturday',
	'general.sunday',
];

const formatTime = (hour, minute) => {
	const h = String(hour || 0).padStart(2, '0');
	const m = String(minute || 0).padStart(2, '0');
	return `${h}:${m}`;
};

const formatSlot = timeslot => {
	const dayKey = DAY_KEYS[timeslot.day];
	const day = dayKey ? I18n.t(dayKey) : '';
	return `${day} ${I18n.t('partner.export.timeslot_binding.starting')} ${formatTime(
		timeslot.hour,
		timeslot.minute
	)}`.trim();
};

// Popup de configuration des créneaux d'UN produit (contrôlée par la page).
// Modèle produit uniquement : chaque règle est { idProduct, idTimeslot, rule }.
class BindingModal extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			// { [timeslotId]: 'enable' | 'disable' } : seuls les créneaux cochés sont présents
			selection: {},
			copyOpen: false,
			copyInput: '',
		};
	}

	componentDidUpdate(prevProps) {
		// (re)initialise la sélection à l'ouverture, ou si le produit ciblé change.
		const opened = this.props.open && !prevProps.open;
		const productChanged =
			this.props.product &&
			prevProps.product &&
			!sameId(this.props.product.id, prevProps.product.id);
		if (opened || productChanged) {
			this.setState({
				selection: this.getInitialSelection(),
				copyOpen: false,
				copyInput: '',
			});
		}
	}

	getInitialSelection = () => {
		const { rules = [], product, multi } = this.props;
		// en mode multi on part toujours d'une sélection vierge : les mêmes règles
		// seront appliquées à tous les produits cochés.
		if (multi || !product) return {};
		return rules
			.filter(rule => sameId(rule.idProduct, product.id))
			.reduce((acc, rule) => {
				acc[rule.idTimeslot] = rule.rule || DEFAULT_MODE;
				return acc;
			}, {});
	};

	toggleTimeslot = timeslotId => {
		this.setState(prevState => {
			const selection = { ...prevState.selection };
			if (Object.prototype.hasOwnProperty.call(selection, timeslotId)) {
				delete selection[timeslotId];
			} else {
				selection[timeslotId] = DEFAULT_MODE;
			}
			return { selection };
		});
	};

	changeMode = (timeslotId, mode) => {
		this.setState(prevState => ({
			selection: { ...prevState.selection, [timeslotId]: mode },
		}));
	};

	onReset = () => this.setState({ selection: {} });

	getSelectionForProduct = idProduct =>
		this.props.rules
			.filter(rule => sameId(rule.idProduct, idProduct))
			.reduce((acc, rule) => {
				acc[rule.idTimeslot] = rule.rule || DEFAULT_MODE;
				return acc;
			}, {});

	toggleCopy = () =>
		this.setState(prevState => ({
			copyOpen: !prevState.copyOpen,
			copyInput: '',
		}));

	onCopyInput = e => this.setState({ copyInput: e.target.value });

	getCopyCandidates = () => {
		const { configuredByProduct, flatProducts, product } = this.props;
		const search = this.state.copyInput.trim().toLowerCase();
		return Object.keys(configuredByProduct)
			.filter(id => !product || !sameId(id, product.id))
			.map(id => {
				const p = flatProducts[id];
				return {
					id,
					name: p ? p.name : `#${id}`,
					count: (configuredByProduct[id] || {}).total || 0,
				};
			})
			.filter(p => !search || (p.name || '').toLowerCase().includes(search))
			.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
			.slice(0, 8);
	};

	onCopyFrom = candidate => {
		this.setState({
			selection: this.getSelectionForProduct(candidate.id),
			copyOpen: false,
			copyInput: '',
		});
		this.props.notify(
			I18n.t('partner.export.timeslot_binding.rules_copied', {
				name: candidate.name,
			}),
			'info'
		);
	};

	save = () => {
		const { product, multi, productIds = [], onSaved, onClose } = this.props;
		const rules = Object.entries(this.state.selection).map(
			([idTimeslot, rule]) => ({
				idTimeslot: Number(idTimeslot),
				rule,
			})
		);

		if (multi) {
			// l'écrasement des produits déjà configurés est confirmé en amont
			// (au clic sur « Configurer la sélection »), côté page.
			if (!productIds.length) return;
			this.props.actions.saveTimeslotRulesBulk({ ids: productIds, rules });
			this.props.notify(
				I18n.t('settings.binding.rules_applied', { count: productIds.length }),
				'info'
			);
			(onSaved || onClose)();
			return;
		}

		if (!product) return;
		this.props.actions.saveTimeslotRules({
			type: 'product',
			id: product.id,
			rules,
		});
		onClose();
	};

	render() {
		const {
			open,
			product,
			multi,
			productIds = [],
			timeslots = [],
			onClose,
		} = this.props;
		const { copyOpen, copyInput } = this.state;
		const hasChanges = !deepEqual(
			this.state.selection,
			this.getInitialSelection()
		);
		const candidates = copyOpen ? this.getCopyCandidates() : [];
		const title = multi
			? I18n.t('settings.binding.multi_title', { count: productIds.length })
			: I18n.t('partner.export.timeslot_binding.title', {
					name: (product && product.name) || '',
				});

		return (
			<Dialog onClose={onClose} open={open} fullWidth maxWidth={'sm'}>
				<DialogTitle>{title}</DialogTitle>

				<DialogContent>
					<WeeklyAvailability
						timeslots={timeslots}
						selection={this.state.selection}
					/>

					<div className="timeslot-binding-toolbar">
						<Button
							size="small"
							startIcon={<FileCopyIcon fontSize="small" />}
							onClick={this.toggleCopy}
							className={copyOpen ? 'is-open' : ''}>
							{I18n.t('partner.export.timeslot_binding.copy_from')}
						</Button>
						<Button
							size="small"
							onClick={this.onReset}
							className="timeslot-binding-reset">
							{I18n.t('partner.export.timeslot_binding.reset')}
						</Button>
					</div>

					{copyOpen ? (
						<div className="timeslot-binding-copy">
							<div className="timeslot-binding-copy-search">
								<SearchIcon fontSize="small" />
								<input
									type="text"
									autoFocus
									placeholder={I18n.t(
										'partner.export.timeslot_binding.copy_search_placeholder'
									)}
									value={copyInput}
									onChange={this.onCopyInput}
								/>
							</div>
							<div className="timeslot-binding-copy-list">
								{candidates.length ? (
									candidates.map(candidate => (
										<div
											key={candidate.id}
											className="timeslot-binding-copy-item"
											onClick={() => this.onCopyFrom(candidate)}>
											<span className="timeslot-binding-copy-name">
												{candidate.name}
											</span>
											<span className="timeslot-binding-copy-count">
												{I18n.t('settings.binding.configured_count', {
													count: candidate.count,
												})}
											</span>
										</div>
									))
								) : (
									<p className="timeslot-binding-copy-empty">
										{I18n.t('partner.export.timeslot_binding.copy_no_result')}
									</p>
								)}
							</div>
						</div>
					) : null}

					<div className={'timeslot-binding-list'}>
						{timeslots.map(timeslot => {
							const checked = Object.prototype.hasOwnProperty.call(
								this.state.selection,
								timeslot.id
							);
							return (
								<div key={timeslot.id} className={'timeslot-binding-row'}>
									<div className={'timeslot-binding-row-main'}>
										<label className={'timeslot-binding-label'}>
											<Checkbox
												color="primary"
												checked={checked}
												onChange={() => this.toggleTimeslot(timeslot.id)}
											/>
											<span className={'timeslot-binding-name'}>
												{formatSlot(timeslot)}
											</span>
											<span className={'timeslot-binding-detail'}>
												({timeslot.name})
											</span>
										</label>
										{checked ? (
											<Select
												className={'timeslot-binding-select'}
												value={this.state.selection[timeslot.id]}
												onChange={e =>
													this.changeMode(timeslot.id, e.target.value)
												}>
												<MenuItem value={TIMESLOT_MODE.ENABLE}>
													{I18n.t('partner.export.timeslot_binding.enable')}
												</MenuItem>
												<MenuItem value={TIMESLOT_MODE.DISABLE}>
													{I18n.t('partner.export.timeslot_binding.disable')}
												</MenuItem>
											</Select>
										) : null}
									</div>
								</div>
							);
						})}
					</div>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose} className={'button-cancel'}>
						{I18n.t('general.cancel')}
					</Button>
					<Button
						onClick={this.save}
						color="primary"
						variant="contained"
						disabled={!hasChanges || (multi && !productIds.length)}
						className={'button-valid'}>
						{multi
							? I18n.t('settings.binding.apply_to_selection')
							: I18n.t('general.save')}
					</Button>
				</DialogActions>
			</Dialog>
		);
	}
}

BindingModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	// produit ciblé : { id, name }. id === id_product (clé des règles).
	product: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		name: PropTypes.string,
	}),
	// mode multi : applique les mêmes règles à tous les produits de productIds.
	multi: PropTypes.bool,
	productIds: PropTypes.arrayOf(
		PropTypes.oneOfType([PropTypes.number, PropTypes.string])
	),
	onSaved: PropTypes.func,
};

const mapStateToProps = state => {
	const timeslot = (state.service && state.service.timeslot) || {};
	return {
		timeslots: timeslot.timeslots || [],
		rules: timeslot.rules || [],
		configuredByProduct: getConfiguredByProduct(state),
		flatProducts: getFlatProducts(state),
	};
};

const mapDispatchToProps = dispatch => ({
	actions: bindActionCreators(timeslotActions, dispatch),
	notify: bindActionCreators(notify, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(BindingModal);
